/**
 * Get the most recent release history entry from a list
 * @param {Array} history - Release history array
 * @returns {Object|null} - Latest release entry or null
 */
export const getMaxReleaseHistory = (history) =>
  history?.reduce(
    (latest, item) =>
      !latest || new Date(item.actionDate) > new Date(latest.actionDate)
        ? item
        : latest,
    null
  );

/**
 * Check if a file is selected in the selectedFiles array
 * @param {Array} selectedFiles - Array of selected files
 * @param {string} id - File id to check
 * @returns {boolean}
 */
export const isSelected = (selectedFiles, id) =>
  selectedFiles.some((f) => f.id === id);

/**
 * Toggle a single item in the selected files list
 * @param {Array} currentSelected - Current selection array
 * @param {Object} item - Item to toggle
 * @returns {Array} - New selection array
 */
export const toggleOneItem = (currentSelected, item) => {
  const exists = currentSelected.some((f) => f.id === item.id);
  return exists
    ? currentSelected.filter((f) => f.id !== item.id)
    : [...currentSelected, item];
};

/**
 * Toggle all items in the selectable items list
 * @param {Array} currentSelected - Current selection array
 * @param {Array} selectableItems - All available items to select
 * @returns {Array} - New selection array
 */
export const toggleAllItems = (currentSelected, selectableItems) => {
  const allSelected = selectableItems.every((a) =>
    currentSelected.some((f) => f.id === a.id)
  );
  if (allSelected)
    return currentSelected.filter(
      (f) => !selectableItems.some((a) => a.id === f.id)
    );
  const newOnes = selectableItems.filter(
    (a) => !currentSelected.some((f) => f.id === a.id)
  );
  return [...currentSelected, ...newOnes];
};

/**
 * Calculate if all items are selected
 * @param {Array} selectableItems - All available items
 * @param {Array} selectedFiles - Currently selected files
 * @returns {boolean}
 */
export const areAllSelected = (selectableItems, selectedFiles) =>
  selectableItems.length > 0 &&
  selectableItems.every((a) => selectedFiles.has(a.id));

/**
 * Calculate if some (but not all) items are selected
 * @param {Array} selectableItems - All available items
 * @param {Array} selectedFiles - Currently selected files
 * @param {boolean} allSelected - Whether all items are selected
 * @returns {boolean}
 */
export const areSomeSelected = (selectableItems, selectedFiles, allSelected) =>
  selectableItems.some((a) => selectedFiles.has(a.id)) && !allSelected;

/**
 * Extract relevant dates from an attachment
 * @param {Object} attachment - Attachment object
 * @returns {Object} - Object with deliveryDate and releaseDate
 */
export const extractAttachmentDates = (attachment) => {
  const deliveryDate = attachment.deliveryControl?.completionDates?.received;
  const releaseDate = attachment.releaseManagement?.channel
    ? getMaxReleaseHistory(attachment.releaseManagement?.history)?.actionDate
    : null;

  return { deliveryDate, releaseDate };
};

/**
 * Build the file download object
 * @param {Object} attachment - Attachment object
 * @param {string} contentId - Content ID from project sheet
 * @returns {Object} - File download object
 */
export const buildFileDownloadObject = (attachment, contentId) => ({
  fileData: attachment,
  contentId,
  origin: "adendos",
  mode: 0,
});

/**
 * Create a selectable item from an addendum
 * @param {Object} addendum - Addendum object
 * @param {boolean} [isChapterAddendum=false] - Whether this addendum belongs to a
 * chapter (long-form dramaturgy, uses the chapter associations lookup) as
 * opposed to a material/episode (uses the episode associations lookup).
 * @returns {Object} - Selectable item
 */
export const createSelectableAddendumItem = (
  addendum,
  isChapterAddendum = false
) => ({
  ...addendum.attachment,
  addendumId: addendum.id,
  type: "Adendo",
  isChapterAddendum,
});

/**
 * Create addenda map for selection purposes
 * @param {Array} addenda - List of addenda
 * @param {boolean} [isChapterAddendum=false] - See `createSelectableAddendumItem`.
 * @returns {Array} - Array of selectable items
 */
export const createSelectableAddendaList = (
  addenda,
  isChapterAddendum = false
) => addenda.map((a) => createSelectableAddendumItem(a, isChapterAddendum));
